import clsx from 'clsx';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import { Menu, MenuItem } from '@/components/dropdownMenu/dropdownMenu';
import { Input } from '@/components/input/input';
import { useAgentsQuery } from '@/hooks/queries/useAgents';
import { useLocalZonePreference } from '@/hooks/useLocalZonePreference';
import { SOURCE_TYPES, SourceTypeEnum } from '@/types/evals';

import { ExampleTypeSelect } from './ExampleTypeSelect';
import styles from './modalSourceCreate.module.scss';

export type PickerZone = 'local' | 'utc';

export const toUtcIso = ( pickerValue: string, zone: PickerZone ): string | undefined => {
  const dt = DateTime.fromISO( pickerValue, { zone } );
  if ( !dt.isValid ) return undefined;
  return dt.toUTC().toISO()!;
};

export const toPickerValue = ( storedUtcIso: string | null | undefined, zone: PickerZone ): string | undefined => {
  if ( !storedUtcIso ) return undefined;
  const dt = DateTime.fromISO( storedUtcIso, { zone } );
  if ( !dt.isValid ) return undefined;
  return dt.startOf( 'minute' ).toISO( { suppressSeconds: true, suppressMilliseconds: true, includeOffset: false } )!;
};

const getPickerZone = (): PickerZone => {
  try { return localStorage.getItem( 'useLocalZone' ) !== 'false' ? 'local' : 'utc'; }
  catch { return 'local'; }
};

type LogType = 'agent' | 'model' | 'completion';

export const LOG_TYPE_CONFIG: Record<LogType, {
  sourceType: SourceTypeEnum;
  title: string;
}> = {
  agent: {
    sourceType: SOURCE_TYPES.AGENT_ACTION_LOGS,
    title: 'Agent Action Log'
  },
  model: {
    sourceType: SOURCE_TYPES.MODEL_INVOCATION_LOGS,
    title: 'Model Invocation Log'
  },
  completion: {
    sourceType: SOURCE_TYPES.COMPLETION_LOGS,
    title: 'Request/Response Log'
  }
};

export const logTypeForSourceType = ( sourceType: SourceTypeEnum ): LogType | undefined => {
  switch ( sourceType ) {
    case SOURCE_TYPES.AGENT_ACTION_LOGS: return 'agent';
    case SOURCE_TYPES.MODEL_INVOCATION_LOGS: return 'model';
    case SOURCE_TYPES.COMPLETION_LOGS: return 'completion';
    default: return undefined;
  }
};

// Remove empty/undefined filter values, converts datetime-local picker values
// to UTC ISO, and rewrites a bare (old-SDK) agent_id into an agent_name
// filter. Old agents have agent_id NULL in model_invocation_log — filtering
// by agent_id would miss their rows, so we route the bare name through
// agent_name instead.
export const cleanMasConfig = (
  config: { filters?: Record<string, unknown>; row_limit?: unknown },
  zone?: PickerZone
) => {
  const pickerZone = zone ?? getPickerZone();
  const filters = config.filters ?? {};
  const cleaned: Record<string, unknown> = {};
  for ( const [ key, value ] of Object.entries( filters ) ) {
    if ( value === '' || value === undefined || value === null ) continue;
    if ( ( key === 'from_start_time' || key === 'to_end_time' ) && typeof value === 'string' ) {
      cleaned[ key ] = toUtcIso( value, pickerZone ) ?? value;
    } else {
      cleaned[ key ] = value;
    }
  }
  const agentId = cleaned.agent_id;
  if ( typeof agentId === 'string' && !agentId.includes( '.' ) && !cleaned.agent_name ) {
    cleaned.agent_name = agentId;
    delete cleaned.agent_id;
  }
  return {
    ...config,
    filters: cleaned,
    row_limit: isNaN( config.row_limit as number ) ? undefined : config.row_limit
  };
};

type MasLogsConfigFieldsProps = {
  logType: LogType;
  isPending?: boolean;
  isVersionMode?: boolean;
};

export const MasLogsConfigFields: React.FC<MasLogsConfigFieldsProps> = ( {
  logType,
  isPending,
  isVersionMode
} ) => {
  const { register, getValues, watch, setValue, formState: { errors } } = useFormContext();

  const { title } = LOG_TYPE_CONFIG[ logType ];

  const { data: agentsData } = useAgentsQuery( { enabled: true } );

  const [ useLocalZone ] = useLocalZonePreference();
  const pickerZone: PickerZone = useLocalZone ? 'local' : 'utc';
  const zoneLabel = useLocalZone ? DateTime.local().toFormat( 'ZZZZ' ) : 'UTC';

  // Stored values are UTC ISO with trailing Z. HTML5 datetime-local rejects
  // the Z suffix, so convert back to a bare wall-clock string on first mount.
  const hasHydrated = useRef( false );
  useEffect( () => {
    if ( hasHydrated.current ) return;
    hasHydrated.current = true;
    const from = getValues( 'config.filters.from_start_time' );
    const to = getValues( 'config.filters.to_end_time' );
    if ( typeof from === 'string' && from.endsWith( 'Z' ) ) {
      setValue( 'config.filters.from_start_time', toPickerValue( from, pickerZone ) ?? '' );
    }
    if ( typeof to === 'string' && to.endsWith( 'Z' ) ) {
      setValue( 'config.filters.to_end_time', toPickerValue( to, pickerZone ) ?? '' );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [] );

  const watchConstellationName = watch( 'config.filters.constellation_name' ) as string | undefined;
  const watchAgentName = watch( 'config.filters.agent_name' ) as string | undefined;
  const watchAgentId = watch( 'config.filters.agent_id' ) as string | undefined;

  const isConstellationDisabled = !!watchAgentId || isPending;
  const isAgentNameDisabled = !!watchAgentId || !watchConstellationName || isPending;
  const isAgentIdDisabled = !!( watchConstellationName || watchAgentName ) || isPending;

  const constellationOptions = useMemo( () => {
    const names = ( agentsData ?? [] )
      .map( a => a.constellationName )
      .filter( ( name ): name is string => !!name );
    return [ ...new Set( names ) ].sort();
  }, [ agentsData ] );

  const agentNameOptions = useMemo( () => {
    let agents = agentsData ?? [];
    if ( watchConstellationName ) {
      agents = agents.filter( a => a.constellationName === watchConstellationName );
    }
    const names = agents.map( a => a.name ).filter( ( name ): name is string => !!name );
    return [ ...new Set( names ) ].sort();
  }, [ agentsData, watchConstellationName ] );

  const agentIdOptions = useMemo( () => {
    const ids = ( agentsData ?? [] ).map( a => a.id ).filter( Boolean );
    return [ ...new Set( ids ) ].sort();
  }, [ agentsData ] );

  // Agent Name is only shown (and only valid) within a constellation. Clearing
  // the constellation hides the field, so drop any stale agent_name value that
  // would otherwise fail validation invisibly.
  useEffect( () => {
    if ( !watchConstellationName && watchAgentName ) {
      setValue( 'config.filters.agent_name', '' );
    }
  }, [ watchConstellationName, watchAgentName, setValue ] );

  type FilterErrors = {
    constellation_name?: { message?: string };
    agent_name?: { message?: string };
    agent_id?: { message?: string };
    message?: string;
  };
  const filtersErrors = ( errors.config as { filters?: FilterErrors } )?.filters;
  const constellationNameError = filtersErrors?.constellation_name;
  const agentNameError = filtersErrors?.agent_name;
  const agentIdError = filtersErrors?.agent_id;
  const rowLimitError = ( errors.config as { row_limit?: { message?: string } } )?.row_limit;

  // Searchable select over the known agents/constellations that also accepts
  // free text — values outside the list are committed via Enter or the
  // 'Use "…"' item.
  const renderFilterSelect = ( opts: {
    field: 'config.filters.constellation_name' | 'config.filters.agent_id' | 'config.filters.agent_name';
    label: string;
    value: string | undefined;
    options: string[];
    disabled: boolean | undefined;
    placeholder: string;
    testId: string;
    error?: { message?: string };
  } ) => (
    <div className={ clsx( 'layout__col', 'pad' ) }>
      <label className="auth_flow_label">{opts.label}</label>
      <Menu
        variant="select"
        searchable
        creatable
        onCreate={ ( value ) => setValue( opts.field, value, { shouldValidate: true } ) }
        onClear={ opts.value ? () => setValue( opts.field, '', { shouldValidate: true } ) : undefined }
        className={ clsx( styles.source_type_select, !opts.value && styles.source_type_placeholder ) }
        value={ opts.value || opts.placeholder }
        disabled={ opts.disabled }
        data-testid={ opts.testId }
      >
        {opts.options.map( ( option ) => (
          <MenuItem
            key={ option }
            value={ option }
            data-testid={ `${ opts.testId }-option-${ option }` }
            onClick={ () => setValue( opts.field, option, { shouldValidate: true } ) }
          />
        ) )}
      </Menu>
      {opts.error && (
        <div className={ styles.error_message }>{opts.error.message}</div>
      )}
    </div>
  );

  return (
    <section className={ styles.config_section }>
      <h3 className={ styles.config_section_title }>
        Configure {title} Properties <span className={ styles.required_asterisk }>*</span>
      </h3>

      <div className={ styles.identifier_group } data-testid="agent-identifier-group">
        <div className={ styles.identifier_row }>
          {renderFilterSelect( {
            field: 'config.filters.constellation_name',
            label: 'Constellation Name',
            value: watchConstellationName,
            options: constellationOptions,
            disabled: isConstellationDisabled,
            placeholder: isConstellationDisabled ? 'Disabled — using agent ID' : 'Select or enter a constellation',
            testId: 'source-config-constellation-name',
            error: constellationNameError
          } )}

          <div className={ styles.identifier_or } aria-hidden>Or</div>

          {renderFilterSelect( {
            field: 'config.filters.agent_id',
            label: 'Agent ID',
            value: watchAgentId,
            options: agentIdOptions,
            disabled: isAgentIdDisabled,
            placeholder: isAgentIdDisabled ? 'Disabled — using constellation/agent name' : 'Select or enter an agent ID',
            testId: 'source-config-agent-id',
            error: agentIdError
          } )}
        </div>

        {watchConstellationName && (
          <div className={ styles.identifier_row }>
            {renderFilterSelect( {
              field: 'config.filters.agent_name',
              label: 'Agent Name',
              value: watchAgentName,
              options: agentNameOptions,
              disabled: isAgentNameDisabled,
              placeholder: 'Select or enter an agent name',
              testId: 'source-config-agent-name',
              error: agentNameError
            } )}
          </div>
        )}

        <div className={ styles.config_grid }>
          <div className={ clsx( 'layout__col', 'pad' ) }>
            <label className="auth_flow_label" htmlFor="config.filters.client_id">
              Client ID
            </label>
            <Input
              id="config.filters.client_id"
              type="text"
              data-testid="source-config-client-id"
              disabled={ isPending }
              { ...register( 'config.filters.client_id' ) }
            />
          </div>

          <div className={ clsx( 'layout__col', 'pad' ) }>
            <label className="auth_flow_label" htmlFor="config.filters.request_id">
              Request ID
            </label>
            <Input
              id="config.filters.request_id"
              type="text"
              data-testid="source-config-request-id"
              disabled={ isPending }
              { ...register( 'config.filters.request_id' ) }
            />
          </div>

          <div className={ clsx( 'layout__col', 'pad' ) }>
            <label className="auth_flow_label" htmlFor="config.filters.correlation_id">
              Correlation ID
            </label>
            <Input
              id="config.filters.correlation_id"
              type="text"
              data-testid="source-config-correlation-id"
              disabled={ isPending }
              { ...register( 'config.filters.correlation_id' ) }
            />
          </div>

          <div className={ clsx( 'layout__col', 'pad' ) }>
            <label className="auth_flow_label" htmlFor="config.filters.constellation_version">
              Constellation Version
            </label>
            <Input
              id="config.filters.constellation_version"
              type="text"
              data-testid="source-config-constellation-version"
              disabled={ isPending }
              { ...register( 'config.filters.constellation_version' ) }
            />
          </div>

          {logType === 'agent' && (
            <div className={ clsx( 'layout__col', 'pad' ) }>
              <label className="auth_flow_label" htmlFor="config.filters.component_name">
                Component Name
              </label>
              <Input
                id="config.filters.component_name"
                type="text"
                data-testid="source-config-component-name"
                disabled={ isPending }
                { ...register( 'config.filters.component_name' ) }
              />
            </div>
          )}

          {logType === 'agent' && (
            <div className={ clsx( 'layout__col', 'pad' ) }>
              <label className="auth_flow_label" htmlFor="config.filters.metadata">
                Metadata
              </label>
              <Input
                id="config.filters.metadata"
                type="text"
                placeholder={ 'Optional JSON, e.g. {"key":"value"}' }
                data-testid="source-config-metadata"
                disabled={ isPending }
                { ...register( 'config.filters.metadata', {
                  setValueAs: ( value ) => {
                    if ( typeof value !== 'string' || value.trim() === '' ) return undefined;
                    try {
                      return JSON.parse( value );
                    } catch {
                      return value;
                    }
                  }
                } ) }
              />
            </div>
          )}

          {logType === 'model' && (
            <div className={ clsx( 'layout__col', 'pad' ) }>
              <label className="auth_flow_label" htmlFor="config.filters.model_id">
                Model ID
              </label>
              <Input
                id="config.filters.model_id"
                type="text"
                data-testid="source-config-model-id"
                disabled={ isPending }
                { ...register( 'config.filters.model_id' ) }
              />
            </div>
          )}

          <div className={ clsx( 'layout__col', 'pad' ) }>
            <label className="auth_flow_label" htmlFor="config.filters.from_start_time">
              From start time ({ zoneLabel })
            </label>
            <Input
              id="config.filters.from_start_time"
              type="datetime-local"
              data-testid="source-config-from-start-time"
              disabled={ isPending }
              { ...register( 'config.filters.from_start_time' ) }
            />
          </div>

          <div className={ clsx( 'layout__col', 'pad' ) }>
            <label className="auth_flow_label" htmlFor="config.filters.to_end_time">
              To end time ({ zoneLabel })
            </label>
            <Input
              id="config.filters.to_end_time"
              type="datetime-local"
              data-testid="source-config-to-end-time"
              disabled={ isPending }
              { ...register( 'config.filters.to_end_time' ) }
            />
          </div>
        </div>
      </div>

      <div className={ styles.config_grid }>
        <div className={ clsx( 'layout__col', 'pad' ) }>
          <label className="auth_flow_label" htmlFor="config.row_limit">
            Row Limit
          </label>
          <Input
            id="config.row_limit"
            type="number"
            placeholder="Optional"
            data-testid="source-config-row-limit"
            disabled={ isPending }
            error={ rowLimitError?.message }
            { ...register( 'config.row_limit',
              { setValueAs: ( value ) => ( value === '' || Number.isNaN( value ) ? undefined : Number( value ) ) }
            ) }
          />
        </div>

        {logType === 'completion' && (
          <ExampleTypeSelect disabled={ isVersionMode || isPending } />
        )}
      </div>

      {filtersErrors && !constellationNameError && !agentNameError && !agentIdError && (
        <div className={ clsx( 'layout__row', 'pad' ) }>
          <div className={ styles.error_message } data-testid="filters-error-message">
            {filtersErrors.message || 'At least one filter must be provided'}
          </div>
        </div>
      )}
    </section>
  );
};
